import { useId } from "react";
import { BRAND_MARK } from "@/lib/brandMark";

type PulsoMarkProps = {
  size?: number;
  /** Cor do anel externo, do núcleo e da varredura. */
  on?: string;
  /** Cor do anel interno e do ponto na ponta da varredura. */
  accent?: string;
  /** Liga as animações (anéis de ping, varredura girando, núcleo pulsante). */
  live?: boolean;
  className?: string;
};

/**
 * Logomarca PULSO — radar vivo: anéis concêntricos + varredura girando +
 * ponto na ponta + núcleo pulsante. Componente autocontido (só CSS global),
 * para funcionar também no splash de boot e no carregamento.
 *
 * O feixe é um gradiente que nasce forte no pivô e se dissolve na ponta, em vez
 * de um preenchimento chapado: é o que faz a rotação ser percebida de relance.
 */
export default function PulsoMark({
  size = 64,
  on = "var(--brand-mark-ink)",
  accent = "var(--brand-mark-accent)",
  live = true,
  className,
}: PulsoMarkProps) {
  // ids únicos: o mark é renderizado várias vezes na mesma página (splash,
  // carregamento, cabeçalhos) e gradientes com id fixo colidiriam entre si.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const beamId = `pulso-beam-${uid}`;
  const glowId = `pulso-glow-${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={BRAND_MARK.viewBox}
      className={className ? `block shrink-0 ${className}` : "block shrink-0"}
      style={{ overflow: "visible" }}
      aria-hidden="true"
    >
      <defs>
        {/* Feixe: opaco no pivô, dissolvendo até a ponta da varredura. */}
        <linearGradient id={beamId} gradientUnits="userSpaceOnUse" x1="50" y1="50" x2="50" y2="2">
          <stop offset="0%" stopColor={on} stopOpacity="0.44" />
          <stop offset="55%" stopColor={on} stopOpacity="0.26" />
          <stop offset="100%" stopColor={on} stopOpacity="0.08" />
        </linearGradient>
        {/* Halo suave do ponto que corre na borda. */}
        <radialGradient id={glowId}>
          <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      {live && (
        <>
          <circle className="pulso-ping" cx="50" cy="50" r="50" fill="none" stroke={accent} strokeWidth="1.6" />
          <circle className="pulso-ping pulso-ping-2" cx="50" cy="50" r="50" fill="none" stroke={accent} strokeWidth="1.6" />
        </>
      )}
      <circle cx="50" cy="50" r={BRAND_MARK.outerRadius} fill="none" stroke={on} strokeWidth={BRAND_MARK.ringWidth} opacity="0.22" />
      <circle cx="50" cy="50" r={BRAND_MARK.innerRadius} fill="none" stroke={accent} strokeWidth={BRAND_MARK.ringWidth} opacity="0.85" />
      <g className={live ? "pulso-sweep" : undefined}>
        <path d={BRAND_MARK.sweepPath} fill={`url(#${beamId})`} />
        <circle cx="50" cy={BRAND_MARK.blipY} r={BRAND_MARK.blipRadius * 3.2} fill={`url(#${glowId})`} />
        <circle className={live ? "pulso-blip" : undefined} cx="50" cy={BRAND_MARK.blipY} r={BRAND_MARK.blipRadius} fill={accent} />
      </g>
      <circle className={live ? "pulso-core" : undefined} cx="50" cy="50" r={BRAND_MARK.coreRadius} fill={on} />
    </svg>
  );
}
